import { createContext, useContext, useEffect, useState } from "react";
import { ScreenContextProps, ScreenProps, ScreenSizeProps } from "./types";

const breakpoints: { [key in ScreenSizeProps]: number } = {
  mobileS: 320,
  mobileM: 375,
  mobileL: 425,
  tablet: 768,
  desktopS: 1024,
  desktopM: 1440,
  desktopL: 2560,
};

const ScreenSizeContext = createContext<ScreenContextProps>({
  screenSize: "mobileS",
  screenType: "mobile",
});

export const ScreenProvider: React.FC<ScreenProps> = ({ children }) => {
  const [screenSize, setScreenSize] = useState<ScreenSizeProps>("mobileS");

  const getScreenSize = (width: number): ScreenSizeProps => {
    if (width < breakpoints.mobileM) return "mobileS";
    if (width < breakpoints.mobileL) return "mobileM";
    if (width < breakpoints.tablet) return "mobileL";
    if (width < breakpoints.desktopS) return "tablet";
    if (width < breakpoints.desktopM) return "desktopS";
    if (width < breakpoints.desktopL) return "desktopM";
    return "desktopL";
  };

  const screenType = ["mobileS", "mobileM", "mobileL"].includes(screenSize)
    ? "mobile"
    : "desktop";

  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== "undefined") {
        setScreenSize(getScreenSize(window.innerWidth));
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <ScreenSizeContext.Provider value={{ screenSize, screenType }}>
      {children}
    </ScreenSizeContext.Provider>
  );
};

export const useScreen = (): ScreenContextProps =>
  useContext(ScreenSizeContext);
